/**
 * What the shell shares with every merchant page: the workspace snapshot
 * (profile, Production Lines, videos — one server read), the business
 * identity the sidebar shows, and the toast.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { useWorkspaceSnapshot, type WorkspaceSnapshot } from './data.js';

interface MerchantWorkspaceShellValue {
  readonly inside: boolean;
  readonly businessName: string | null;
  readonly profileConfirmed: boolean | null;
  readonly setBusinessName: (name: string | null) => void;
  readonly setProfileConfirmed: (confirmed: boolean | null) => void;
  readonly snapshot: WorkspaceSnapshot | null;
}

const EMPTY_SNAPSHOT: WorkspaceSnapshot = {
  loading: false,
  failed: false,
  profile: null,
  rawLines: [],
  pendingLines: [],
  lines: [],
  videos: [],
  rawVideos: [],
  creationDestination: null,
  refresh: () => Promise.resolve(null),
};

const MerchantWorkspaceShellContext = createContext<MerchantWorkspaceShellValue>({
  inside: false,
  businessName: null,
  profileConfirmed: null,
  setBusinessName: () => undefined,
  setProfileConfirmed: () => undefined,
  snapshot: null,
});

export function MerchantWorkspaceShellProvider({
  children,
}: {
  readonly children: ReactNode;
}): ReactNode {
  const [businessName, setBusinessName] = useState<string | null>(null);
  const [profileConfirmed, setProfileConfirmed] = useState<boolean | null>(null);
  const snapshot = useWorkspaceSnapshot(true);

  // The snapshot names the business; pages may override it as they learn
  // more (a fresh intake's display name arrives before the profile does).
  useEffect(() => {
    const name = snapshot.profile?.business.displayName;
    if (name !== undefined) setBusinessName(name);
    if (snapshot.profile !== null) {
      setProfileConfirmed(snapshot.profile.profile?.reviewState === 'APPROVED');
    }
  }, [snapshot.profile]);

  const value = useMemo(
    () => ({
      inside: true,
      businessName,
      profileConfirmed,
      setBusinessName,
      setProfileConfirmed,
      snapshot,
    }),
    [businessName, profileConfirmed, snapshot],
  );
  return (
    <MerchantWorkspaceShellContext.Provider value={value}>
      {children}
    </MerchantWorkspaceShellContext.Provider>
  );
}

export function useIsInsideMerchantWorkspaceShell(): boolean {
  return useContext(MerchantWorkspaceShellContext).inside;
}

export function useMerchantWorkspaceIdentity(): Pick<
  MerchantWorkspaceShellValue,
  'businessName' | 'profileConfirmed' | 'setBusinessName' | 'setProfileConfirmed'
> {
  const { businessName, profileConfirmed, setBusinessName, setProfileConfirmed } =
    useContext(MerchantWorkspaceShellContext);
  return { businessName, profileConfirmed, setBusinessName, setProfileConfirmed };
}

/** The shared snapshot; outside the shell it is empty and never loads. */
export function useWorkspace(): WorkspaceSnapshot {
  return useContext(MerchantWorkspaceShellContext).snapshot ?? EMPTY_SNAPSHOT;
}
